<template>
  <div
    class="portal-shortcuts"
    :class="{ 'portal-shortcuts--labeled': isLabeled }"
    role="toolbar"
    aria-label="Quick navigation"
  >
    <button
      v-for="item in shortcutItems"
      :key="item.id"
      type="button"
      class="portal-shortcuts__btn"
      :class="`portal-shortcuts__btn--${item.id}`"
      :aria-label="item.label"
      v-tooltip.bottom-center="isLabeled ? null : tip(item.tooltip)"
      @click="item.action"
    >
      <i :class="item.icon" aria-hidden="true"></i>
      <span v-if="isLabeled" class="portal-shortcuts__label">{{
        item.label
      }}</span>
      <span v-if="isLabeled" class="portal-shortcuts__hint">{{
        item.hint
      }}</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'PortalShortcuts',
  props: {
    variant: {
      type: String,
      default: 'compact',
      validator: (v) => ['compact', 'labeled'].includes(v),
    },
  },
  computed: {
    isLabeled() {
      return this.variant === 'labeled';
    },
    shortcutItems() {
      return [
        {
          id: 'reservations',
          icon: 'icon_grid-3x3',
          label: 'Reserved rooms',
          hint: 'View all room reservations',
          tooltip: 'See All Reserved Rooms View',
          action: this.goAllReservedRooms,
        },
        {
          id: 'takeaway',
          icon: 'icon-food_icon_chinese',
          label: 'Take away',
          hint: 'Takeaway orders & kitchen tickets',
          tooltip: 'Take away customers and kitchen tickets',
          action: this.goTakeaway,
        },
        {
          id: 'table',
          icon: 'icon-food_icon_cloche',
          label: 'Table orders',
          hint: 'Dine-in tables & kitchen tickets',
          tooltip: 'Table orders and kitchen tickets',
          action: this.goTables,
        },
        {
          id: 'tickets',
          icon: 'icon_printer',
          label: 'Tickets',
          hint: 'Customer & KOT tickets',
          tooltip: 'Customer and KOT tickets, cancel orders',
          action: this.goCustomerTickets,
        },
      ];
    },
  },
  methods: {
    tip(content) {
      return {
        content,
        classes: ['portal-tooltip'],
        delay: { show: 120, hide: 80 },
        offset: 6,
      };
    },
    goAllReservedRooms() {
      this.$router.push({ path: '/all-reserved-rooms/' });
    },
    goTakeaway() {
      this.$router.push({ path: '/order/take-away' });
    },
    goTables() {
      this.$router.push({ path: '/tables' });
    },
    goCustomerTickets() {
      this.$router.push({ path: '/customer-tickets' });
    },
  },
};
</script>

<style scoped>
.portal-shortcuts {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.portal-shortcuts__btn {
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid var(--brand-primary-border, rgba(81, 45, 130, 0.22));
  border-radius: 8px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff !important;
  line-height: 1;
  box-shadow: 0 1px 3px var(--brand-shadow-sm, rgba(81, 45, 130, 0.2));
  transition: transform 0.12s ease, box-shadow 0.15s ease;
}

.portal-shortcuts__btn i {
  font-size: 15px;
  margin: 0;
  padding: 0;
  line-height: 1;
}

.portal-shortcuts__btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px var(--brand-shadow-md, rgba(81, 45, 130, 0.28));
}

.portal-shortcuts__btn:active {
  transform: scale(0.96);
}

.portal-shortcuts:not(.portal-shortcuts--labeled) .portal-shortcuts__btn {
  background: #1a6d78 !important;
  border: 0 !important;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.14) !important;
}

.portal-shortcuts__btn--reservations {
  background: linear-gradient(
    180deg,
    var(--brand-primary, #512d82) 0%,
    var(--brand-primary-hover, #3f2366) 100%
  );
}

.portal-shortcuts__btn--takeaway {
  background: linear-gradient(
    180deg,
    var(--brand-primary-light, #6d42a8) 0%,
    var(--brand-primary, #512d82) 100%
  );
}

.portal-shortcuts__btn--table {
  background: linear-gradient(
    180deg,
    var(--brand-primary-mid, #5a3d8a) 0%,
    var(--brand-primary-hover, #452d6e) 100%
  );
}

.portal-shortcuts__btn--tickets {
  background: linear-gradient(
    180deg,
    var(--brand-primary-hover, #3f2366) 0%,
    var(--brand-primary-dark, #2d1849) 100%
  );
}

.portal-shortcuts--labeled {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
  align-content: start;
}

.portal-shortcuts--labeled .portal-shortcuts__btn {
  width: 100%;
  height: auto;
  min-height: 108px;
  max-height: none;
  padding: 14px 10px 12px;
  flex-direction: column;
  gap: 2px;
  border: 0 !important;
  border-radius: 16px;
  background: #1a6d78 !important;
  box-shadow: none !important;
  box-sizing: border-box;
  overflow: hidden;
}

.portal-shortcuts--labeled .portal-shortcuts__btn:hover {
  box-shadow: none !important;
  transform: none;
  filter: brightness(1.06);
}

.portal-shortcuts--labeled .portal-shortcuts__btn i {
  font-size: 26px;
  flex-shrink: 0;
  margin-bottom: 4px;
}

.portal-shortcuts__label {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  color: #fff;
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.portal-shortcuts__hint {
  font-size: 11px;
  font-weight: 500;
  line-height: 1.25;
  text-align: center;
  color: #ffffff;
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@media (max-width: 520px) {
  .portal-shortcuts--labeled {
    grid-template-columns: 1fr 1fr;
  }

  .portal-shortcuts--labeled .portal-shortcuts__btn {
    min-height: 96px;
    padding: 12px 8px 10px;
  }

  .portal-shortcuts--labeled .portal-shortcuts__btn i {
    font-size: 22px;
  }
}
</style>
